/** Shared JSON body shape for POST /tournament/create and fields blob for PUT /updateTournament */

export function mapBallTypeForApi(bt?: string | null): string {
  if (!bt) return "Leather";
  if (bt === "BallType.leather") return "Leather";
  if (bt === "BallType.tennis") return "Tennis";
  if (bt === "BallType.other") return "Other";
  return bt;
}

/** UI match type → PUT /updateTournament `matchType` enum (TEST → TEST_MATCH). */
export function mapMatchTypeForUpdateApi(ui: string): string {
  const u = (ui || "").trim().toUpperCase().replace(/\s+/g, "_");
  if (u === "TEST") return "TEST_MATCH";
  return u;
}

function normalizeCountryCodeForApi(code: unknown): string {
  const s = code != null ? String(code).trim() : "";
  if (!s) return "+91";
  return s.startsWith("+") ? s : `+${s.replace(/^\+/, "")}`;
}

function readBool(draft: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  for (const k of keys) {
    const v = draft[k];
    if (typeof v === "boolean") return v;
  }
  return undefined;
}

function readNumber(draft: Record<string, unknown>, key: string, fallback: number): number {
  const v = draft[key];
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return fallback;
}

/** Build JSON payload for POST /tournament/create */
export function buildCreateTournamentPayload(draft: Record<string, unknown>, selectedLeagueId: string | null) {
  const isOther = draft?.matchType === "OTHER_LIMITED_OVER";
  const isTest = draft?.matchType === "TEST";
  const countryCode = draft?.organizerPhoneNumberCountryCode
    ? String(draft.organizerPhoneNumberCountryCode).startsWith("+")
      ? draft.organizerPhoneNumberCountryCode
      : `+${draft.organizerPhoneNumberCountryCode}`
    : "+91";

  return {
    tournamentName: draft?.tournamentName ?? "",
    description: draft?.description ?? "",
    organizerName: draft?.organizerName ?? "",
    organizerPhoneNumber: draft?.organizerPhoneNumber ?? "",
    organizerPhoneNumberCountryCode: countryCode,
    organizerEmailId: draft?.organizerEmailId ?? "",
    startDate: draft?.startDate ?? "",
    endDate: draft?.endDate ?? "",
    matchType: mapMatchTypeForUpdateApi(String(draft?.matchType ?? "T20")),
    ballType: mapBallTypeForApi(draft?.ballType as string | null),
    tournamentCategory: draft?.tournamentCategory ?? "OTHER",
    leagueId: selectedLeagueId ?? draft?.leagueId ?? "",
    maxOversPerBowler: isTest ? 0 : (draft?.maxOversPerBowler ?? 0),
    maxOvers: isOther ? (draft?.maxOvers ?? 0) : 0,
    maxWickets: isOther ? (draft?.maxWickets ?? 0) : 0,
    maxPowerPlayOvers: typeof draft?.maxPowerPlayOvers === "number" ? draft.maxPowerPlayOvers : 0,
    pointForWin: isTest ? (draft?.pointForOutrightWin ?? 0) : (draft?.pointForWin ?? 0),
    pointForTie: draft?.pointForTie ?? 0,
    pointForAbandoned: draft?.pointForAbandoned ?? 0,
    isRosterLocked: !!draft?.isRosterLocked,
    canEditScoreCard: !!draft?.canEditScoreCard,
    isImpactPlayerAllowed: !!draft?.isImpactPlayerAllowed,
    isAbandonedGameCounted:
      typeof draft?.isAbandonedGameCounted === "boolean" ? draft.isAbandonedGameCounted : true,
    powerPlayEnabled: typeof draft?.powerPlayEnabled === "boolean" ? draft.powerPlayEnabled : true,
    rankingPreference: (draft?.rankingPreference as string) || "TOTAL_POINTS",
    scheduledDays: typeof draft?.scheduledDays === "number" ? draft.scheduledDays : 0,
    tickerAutomationEnabled:
      typeof draft?.tickerAutomationEnabled === "boolean" ? draft.tickerAutomationEnabled : true,
    isTickerAutomationEnabled: !!draft?.isTickerAutomationEnabled,
    bonusPointsEnabled: !!draft?.bonusPointsEnabled,
    isStrictModeEnabled: !!draft?.isStrictModeEnabled,
    isPowerPlayEnabled: !!draft?.isPowerPlayEnabled,
    waiverRequired: !!draft?.waiverRequired,
    waiverId: draft?.waiverId ?? null,
    bannerImage:
      draft?.bannerBase64 && draft?.bannerFileType
        ? { base64Image: draft.bannerBase64, fileType: draft.bannerFileType }
        : null,
    tournamentRules: (() => {
      const rules = (draft?.tournamentRules as Record<string, unknown>) ?? {};
      return isTest ? { ...rules, excludeRules: [], excludeOvers: [] } : rules;
    })(),
    ...(isTest ? {
      pointForFirstInningsLead: draft?.pointForFirstInningsLead ?? null,
      pointForFirstInningsLoss: draft?.pointForFirstInningsLoss ?? null,
      pointForOutrightWin: draft?.pointForOutrightWin ?? null,
      bonusPointForWinByInnings: draft?.bonusPointForWinByInnings ?? null,
    } : {}),
  };
}

/**
 * Fields JSON for PUT /updateTournament (query `fields`).
 * Matches API: snake-free camelCase booleans `isPowerPlayEnabled`, `isTickerAutomationEnabled`, etc.
 * Banner file is sent separately as multipart `bannerUri`.
 */
export function buildUpdateTournamentFields(
  draft: Record<string, unknown>,
  tournamentId: string,
  leagueId: string | null,
) {
  const isOther = draft?.matchType === "OTHER_LIMITED_OVER";
  const isTest = draft?.matchType === "TEST";
  const matchTypeUi = draft?.matchType != null ? String(draft.matchType) : "T20";

  const isAbandoned =
    readBool(draft, "isAbandonedGameCounted") ?? true;
  const tickerOn =
    readBool(draft, "isTickerAutomationEnabled", "tickerAutomationEnabled") ?? true;
  const powerOn =
    readBool(draft, "isPowerPlayEnabled", "powerPlayEnabled") ?? false;

  const ranking =
    typeof draft.rankingPreference === "string" && draft.rankingPreference.trim()
      ? draft.rankingPreference
      : "TOTAL_POINTS";

  return {
    tournamentId,
    tournamentName: draft?.tournamentName ?? "",
    organizerName: draft?.organizerName ?? "",
    organizerPhoneNumber: draft?.organizerPhoneNumber != null ? String(draft.organizerPhoneNumber) : "",
    organizerPhoneNumberCountryCode: normalizeCountryCodeForApi(draft?.organizerPhoneNumberCountryCode),
    organizerEmailId: draft?.organizerEmailId ?? "",
    startDate: draft?.startDate ?? "",
    endDate: draft?.endDate ?? "",
    matchType: mapMatchTypeForUpdateApi(matchTypeUi),
    maxOvers: isOther ? readNumber(draft, "maxOvers", 0) : 0,
    maxOversPerBowler: isTest ? 0 : readNumber(draft, "maxOversPerBowler", 0),
    tournamentCategory: draft?.tournamentCategory ?? "OTHER",
    ballType: mapBallTypeForApi(draft?.ballType as string | null),
    maxWickets: isOther ? readNumber(draft, "maxWickets", 0) : 0,
    leagueId: leagueId != null ? String(leagueId) : String(draft?.leagueId ?? ""),
    description: draft?.description ?? "",
    pointForWin: isTest ? readNumber(draft, "pointForOutrightWin", 0) : readNumber(draft, "pointForWin", 0),
    pointForTie: readNumber(draft, "pointForTie", 0),
    pointForAbandoned: readNumber(draft, "pointForAbandoned", 0),
    isAbandonedGameCounted: isAbandoned,
    isRosterLocked: !!draft?.isRosterLocked,
    canEditScoreCard: !!draft?.canEditScoreCard,
    rankingPreference: ranking,
    isImpactPlayerAllowed: !!draft?.isImpactPlayerAllowed,
    isPowerPlayEnabled: powerOn,
    maxPowerPlayOvers: readNumber(draft, "maxPowerPlayOvers", 0),
    isTickerAutomationEnabled: tickerOn,
    scheduledDays: readNumber(draft, "scheduledDays", 0),
    waiverRequired: !!draft?.waiverRequired,
    waiverId: draft?.waiverId ?? null,
    tournamentRules: (() => {
      const rules = (draft?.tournamentRules as Record<string, unknown>) ?? {};
      return isTest ? { ...rules, excludeRules: [], excludeOvers: [] } : rules;
    })(),
    ...(isTest ? {
      pointForFirstInningsLead: draft?.pointForFirstInningsLead ?? null,
      pointForFirstInningsLoss: draft?.pointForFirstInningsLoss ?? null,
      pointForOutrightWin: draft?.pointForOutrightWin ?? null,
      bonusPointForWinByInnings: draft?.bonusPointForWinByInnings ?? null,
    } : {}),
  };
}
